import { ConnectedPosition } from '@angular/cdk/overlay';
import { SlicePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  NgMultiLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { isEqual, isNull } from 'lodash-es';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

import { SiteSelectDropdownComponent } from '@features/site-select-dropdown/site-select-dropdown.component';

import { InputComponent } from '@shared/components/input/input.component';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { DocumentLibraryService } from '@shared/services/document-library.service';
import { Option } from '@shared/types/components.type';
import { FormGroupTyped } from '@shared/utils/utils';

import { DocumentsPageService } from '../documents-page.service';
import { DocumentsComponentForm } from '../documents.component.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    NgSelectComponent,
    NgMultiLabelTemplateDirective,
    NgOptionTemplateDirective,
    TooltipDirective,
    NoopValueAccessorDirective,
    SiteSelectDropdownComponent,
    SlicePipe,
  ],
  selector: 'aux-document-library-filters',
  styleUrls: ['./document-library-filters.component.css'],
  templateUrl: './document-library-filters.component.html',
})
export class DocumentLibraryFiltersComponent implements AfterViewInit, OnInit {
  documentLibrary = inject(DocumentLibraryService);
  documentsPageService = inject(DocumentsPageService);

  private destroyRef = inject(DestroyRef);

  documentTypeOptions: Option[] = [];
  @Input() form!: FormGroupTyped<DocumentsComponentForm>;
  @Input() onFilterChange!: () => void;
  optionTooltipPositions: ConnectedPosition[] = [
    {
      offsetX: -10,
      offsetY: 8,
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];

  private isResetting = false;

  ngOnInit(): void {
    this.documentTypeOptions = this.documentLibrary.getDocumentOptions();

    this.form.controls.search.valueChanges
      .pipe(
        // Skip trigger filter change event if we already click on set filters
        filter((val) => !isNull(val)),
        distinctUntilChanged(isEqual),
        debounceTime(1000)
      )
      .subscribe(() => {
        this.onFilterChange();
      });
  }

  ngAfterViewInit(): void {
    this.form
      .get('sites')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isResetting) {
          this.onFilterChange();
        }
      });
  }

  castItemsToOptions(items: unknown): Option[] {
    return Array.isArray(items) ? items : [];
  }

  resetAllFilters() {
    this.isResetting = true;

    Object.entries(this.form.controls).forEach(([controlName, control]) => {
      if (controlName !== 'table') {
        control.reset();
      }
    });

    this.onFilterChange();

    setTimeout(() => {
      this.isResetting = false;
    }, 500);
  }
}
